import React, { useEffect, useMemo, useState } from "react";
import { Button, List, Popover, Space } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import {
  buildOrderFromColumnItems,
  moveColumnInOrder,
} from "./column-order-utils";

function ColumnOrderManager({
  columns = [],
  value = null,
  onChange,
  onSave,
  title = "Mover colunas",
  disabled = false,
  lockedKeys = [],
}) {
  const [open, setOpen] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);

  const lockedKeySet = useMemo(
    () => new Set(Array.isArray(lockedKeys) ? lockedKeys : []),
    [lockedKeys],
  );

  const columnMap = useMemo(() => {
    const map = new Map();
    (columns || []).forEach((column) => {
      if (column?.key) map.set(column.key, column);
    });
    return map;
  }, [columns]);

  const resolvedOrder = useMemo(
    () => buildOrderFromColumnItems(columns, value),
    [columns, value],
  );

  useEffect(() => {
    if (open) {
      setDraftOrder(resolvedOrder);
    }
  }, [open, resolvedOrder]);

  const handleMove = (columnKey, direction) => {
    setDraftOrder((prev) => {
      const index = prev.indexOf(columnKey);
      if (index === -1) return prev;
      if (direction < 0 && index === 0) return prev;
      if (direction > 0 && index === prev.length - 1) return prev;
      if (lockedKeySet.has(columnKey)) return prev;
      return moveColumnInOrder(prev, columnKey, direction);
    });
  };

  const handleApply = () => {
    onChange?.(draftOrder);
    onSave?.(draftOrder);
    setOpen(false);
  };

  const handleCancel = () => {
    setDraftOrder(resolvedOrder);
    setOpen(false);
  };

  const content = (
    <div style={{ width: 280 }}>
      <List
        size="small"
        dataSource={draftOrder}
        renderItem={(columnKey, index) => {
          const column = columnMap.get(columnKey);
          if (!column || column.visible === false) return null;

          const isLocked = lockedKeySet.has(columnKey);
          const isFirst = index === 0;
          const isLast = index === draftOrder.length - 1;

          return (
            <List.Item
              style={{ paddingInline: 0 }}
              actions={[
                <Button
                  key="up"
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={disabled || isLocked || isFirst}
                  onClick={() => handleMove(columnKey, -1)}
                />,
                <Button
                  key="down"
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={disabled || isLocked || isLast}
                  onClick={() => handleMove(columnKey, 1)}
                />,
              ]}
            >
              <Space size={8}>
                <HolderOutlined style={{ color: "#012442" }} />
                <span>{column.title || column.key}</span>
              </Space>
            </List.Item>
          );
        }}
      />
      <Space style={{ marginTop: 8, justifyContent: "flex-end", width: "100%" }}>
        <Button size="small" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button size="small" type="primary" onClick={handleApply} disabled={disabled}>
          Aplicar
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={content}
      title={title}
    >
      <Button icon={<HolderOutlined />} disabled={disabled}>
        {title}
      </Button>
    </Popover>
  );
}

export default ColumnOrderManager;
